import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TProductInstance } from '@lib/core/products/types';
import { getProductsListData, getTastePathProductsData } from '@lib/core/products/utils';
import {
  selectRetailerCommunicationStyleOrder,
  selectRetailerMaxLengthTastePathPerCategory,
} from '@lib/core/retailers/selectors/retailer';
import { ApiQueries } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { selectServiceProductCategory } from '@lib/core/service/selectors';
import { TStyleStats } from '@lib/core/service/types/interface';
import { shuffleArray } from '@lib/core/service/utils';
import { selectProductRateData } from '@lib/core/users/selectors/productRate';
import { TProductRate } from '@lib/core/users/slices/productRate';
import { IState, TTastePathData } from '@lib/tools/tastePathProducts/types/types';

const initialState: IState = {
  currentStyleCategoryId: null,
  error: null,
  isTastePathProductsLoading: false,
  nextStyleCategoryId: null,
  tastePathData: null,
};

const getNextStyleCategoryId = ({
  tastePathData,
  currentStyleCategoryId,
}: {
  tastePathData: Record<string, TTastePathData>;
  currentStyleCategoryId: string;
}): string => {
  const categoryIds = Object.keys(tastePathData);
  const currentIndex = categoryIds.indexOf(currentStyleCategoryId);

  if (categoryIds.every(id => tastePathData[id].shouldSkipStyleCategory)) {
    return currentStyleCategoryId;
  }

  let index = currentIndex;

  do {
    index = (index + 1) % categoryIds.length;
    const nextId = categoryIds[index];
    if (!tastePathData[nextId].shouldSkipStyleCategory) {
      return nextId;
    }
  } while (index !== currentIndex);

  return currentStyleCategoryId;
};

const createTastePathData = ({
  products,
  feedbackData,
  sortedStyleStats,
}: {
  products: TProductInstance[];
  feedbackData: TProductRate[];
  sortedStyleStats: TStyleStats;
}) => {
  const tastePathData = Object.fromEntries(
    Object.keys(sortedStyleStats).map(styleId => {
      const filteredByStyleProducts = shuffleArray(
        products.filter(product => product.character?.type?.identifier === styleId),
      );

      const shouldSkipStyleCategory =
        feedbackData.filter(product => product?.product?.character?.type?.identifier === styleId).length >=
        filteredByStyleProducts.length;

      return [
        styleId,
        {
          name: sortedStyleStats[styleId]?.name,
          products: filteredByStyleProducts,
          shouldSkipStyleCategory,
        },
      ];
    }),
  ) as Record<string, TTastePathData>;

  const currentStyleCategoryId = Object.keys(tastePathData).find(id => tastePathData[id].products.length > 0);

  const nextStyleCategoryId = getNextStyleCategoryId({ currentStyleCategoryId, tastePathData });

  return { currentStyleCategoryId, nextStyleCategoryId, tastePathData };
};

export const fetchTastePathProducts = createTypedAsyncThunk('fetchTastePathProducts', async (_, { getState }) => {
  try {
    const state = getState();
    const productCategory = selectServiceProductCategory(state);
    const feedbackData = selectProductRateData(state);
    const maxLength = selectRetailerMaxLengthTastePathPerCategory(state);
    const styleOrder = selectRetailerCommunicationStyleOrder(state);

    const styleStats = ((await getProductsListData({ [ApiQueries.productCategory]: productCategory })).stats?.styles ||
      {}) as TStyleStats;

    const sortedStyleStats: TStyleStats = Object.fromEntries(
      Object.entries(styleStats)
        .filter(([, value]) => value.count > 0)
        .sort(([a], [b]) => {
          const iA = styleOrder.indexOf(a);
          const iB = styleOrder.indexOf(b);
          return (iA === -1 ? Infinity : iA) - (iB === -1 ? Infinity : iB);
        }),
    );

    const responses = await Promise.all(
      Object.keys(sortedStyleStats).map(styleId =>
        getTastePathProductsData({
          [ApiQueries.styles]: styleId,
          limit: maxLength,
        }),
      ),
    );
    const products = responses.flatMap(response => response?.results ?? []);

    const { tastePathData, currentStyleCategoryId, nextStyleCategoryId } = createTastePathData({
      feedbackData,
      products,
      sortedStyleStats,
    });

    return {
      currentStyleCategoryId,
      nextStyleCategoryId,
      tastePathData,
    };
  } catch (error) {
    return Promise.reject(parseError(error));
  }
});

const tastePathProductsSlice = createSlice({
  extraReducers: builder => {
    builder
      .addCase(fetchTastePathProducts.pending, state => {
        state.isTastePathProductsLoading = true;
        state.error = null;
      })
      .addCase(fetchTastePathProducts.fulfilled, (state, { payload }) => {
        const { tastePathData, currentStyleCategoryId, nextStyleCategoryId } = payload;

        state.tastePathData = tastePathData;
        state.currentStyleCategoryId = currentStyleCategoryId;
        state.nextStyleCategoryId = nextStyleCategoryId;
        state.isTastePathProductsLoading = false;
      })
      .addCase(fetchTastePathProducts.rejected, (state, action) => {
        state.isTastePathProductsLoading = false;
        state.error = action.error;
      });
  },
  initialState,
  name: 'tastePath',
  reducers: {
    resetTastePathData() {
      return initialState;
    },
    setNextStyleCategoryId(state) {
      const { tastePathData, currentStyleCategoryId } = state;

      const newCurrentStyleCategoryId =
        tastePathData &&
        getNextStyleCategoryId({
          currentStyleCategoryId,
          tastePathData,
        });

      const nextStyleCategoryId =
        tastePathData &&
        getNextStyleCategoryId({
          currentStyleCategoryId: newCurrentStyleCategoryId,
          tastePathData,
        });

      state.currentStyleCategoryId = newCurrentStyleCategoryId;
      state.nextStyleCategoryId = nextStyleCategoryId;
    },
    setShouldSkipStyleCategory(
      state,
      action: PayloadAction<{
        currentStyleCategoryId: string;
        shouldChangeTastePathCategory: boolean;
      }>,
    ) {
      const { currentStyleCategoryId, shouldChangeTastePathCategory } = action.payload;

      state.tastePathData[currentStyleCategoryId].shouldSkipStyleCategory = shouldChangeTastePathCategory;
    },
  },
});

export const { setShouldSkipStyleCategory, setNextStyleCategoryId, resetTastePathData } =
  tastePathProductsSlice.actions;
export default tastePathProductsSlice.reducer;
